export const SITE = {
  name: 'HyenaMind',
  tagline: 'Marketing Without Borders',
  domain: 'https://hyenamind.com',
  description:
    'HyenaMind helps local contractors and home-service businesses build, market and grow: websites, local SEO, paid ads, lead generation, qualification and follow-up.',
  phone: '(236) 857-3534',
  phoneHref: 'tel:+12368573534',
  email: 'hello@hyenamind.com',
  address: 'Elliott St, Brampton, ON L6Y 1S9, Canada',
  country: 'Canada',
};

export const BRAND = {
  emblem: '/assets/brand/hyena-emblem.png',
  // 111x132 derivatives for the header, footer and inline marks (displayed at 20–44px).
  emblemSmall: '/assets/brand/hyena-emblem-132.png',
  emblemSmallWebp: '/assets/brand/hyena-emblem-132.webp',
  heroEmblem: '/assets/brand/hyena-hero-dark.png',
};

/**
 * Primary navigation.
 *
 * Ordered around what HyenaMind actually is: a growth system for local
 * businesses. "Industries" leads because the ten trade pages are the clearest
 * proof that the system is built per business model, and they previously had no
 * entry point in the header at all. "Services" is grouped by the seven stages
 * rather than by channel, so the menu teaches the system on the way past.
 *
 * The homeowner ecosystem is kept but demoted to NAV_SECONDARY — it is a real
 * part of the business and a tertiary audience, and at equal weight in the
 * primary bar it made the company read as a renovation marketplace.
 */
export const NAV = [
  {
    label: 'Industries',
    to: '/industries',
    cols: 2,
    footer: { label: 'Explore the growth system built around your industry', to: '/industries' },
    mega: [
      {
        title: 'Build & trades',
        links: [
          { label: 'Construction', to: '/industries/construction' },
          { label: 'Home Renovation', to: '/industries/home-renovation' },
          { label: 'Roofing', to: '/industries/roofing' },
          { label: 'Electrical', to: '/industries/electrical' },
          { label: 'Painting', to: '/industries/painting' },
        ],
      },
      {
        title: 'Service & maintenance',
        links: [
          { label: 'HVAC', to: '/industries/hvac' },
          { label: 'Plumbing', to: '/industries/plumbing' },
          { label: 'Cleaning', to: '/industries/cleaning' },
          { label: 'Landscaping', to: '/industries/landscaping' },
          { label: 'Pest Control', to: '/industries/pest-control' },
        ],
      },
    ],
  },
  {
    label: 'Services',
    to: '/services',
    cols: 3,
    footer: { label: 'See how the seven stages connect', to: '/how-it-works' },
    mega: [
      {
        title: '01 — Build',
        links: [
          { label: 'Website Design & Development', to: '/services/web-design-development' },
          { label: 'PHP Migration & Modernization', to: '/php-migration' },
        ],
      },
      {
        title: '02 — Get found',
        links: [
          { label: 'SEO & Local SEO', to: '/services/seo-local-seo' },
        ],
      },
      {
        title: '03 — Attract',
        links: [
          { label: 'Google Ads', to: '/services/google-ads' },
          { label: 'Meta Ads', to: '/services/meta-ads' },
          { label: 'Social Media Marketing', to: '/services/social-media-marketing' },
        ],
      },
      {
        title: '04 — Convert & qualify',
        links: [
          { label: 'Conversion Rate Optimisation', to: '/services/conversion-optimization' },
          { label: 'Lead Generation', to: '/services/lead-generation' },
          { label: 'Lead Qualification', to: '/services/lead-qualification' },
          { label: 'Qualified Leads', to: '/contractors/get-qualified-leads' },
        ],
      },
      {
        title: '05 — Follow up & grow',
        links: [
          { label: 'Appointment Setting & Follow-Up', to: '/services/appointment-setting' },
          { label: 'CRM & Marketing Automation', to: '/services/crm-automation' },
          { label: 'Conversion Tracking & Analytics', to: '/services/tracking-analytics' },
          { label: 'Grow Your Business', to: '/contractors/grow-your-business' },
        ],
      },
      {
        title: 'Everything',
        links: [
          { label: 'All Services', to: '/services' },
          { label: 'All Contractor Services', to: '/contractors' },
        ],
      },
    ],
  },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Resources', to: '/resources' },
  {
    label: 'Tools',
    to: '/tools',
    children: [
      { label: 'All Planning Tools', to: '/tools' },
      { label: 'Renovation Project Planner', to: '/tools/renovation-planner' },
      { label: 'Compare Renovation Quotes', to: '/tools/compare-renovation-quotes' },
      { label: 'Renovation Budget Planner', to: '/tools/renovation-budget-planner' },
      { label: 'Contractor Checklist', to: '/tools/contractor-checklist' },
      { label: 'Renovation Planning Checklist', to: '/tools/renovation-checklist' },
    ],
  },
];

/* Homeowner ecosystem and company pages: present, deliberately quieter. */
export const NAV_HOMEOWNER = [
  { label: 'For Homeowners', to: '/homeowners' },
  { label: 'Renovation Services', to: '/renovations' },
  { label: 'Get Renovation Quotes', to: '/get-renovation-quotes' },
  { label: 'Renovation Cost Estimator', to: '/renovation-cost-estimator' },
];

export const NAV_SECONDARY = [
  { label: 'Case Studies', to: '/case-studies' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export const RENOVATION_TILES = [
  {
    slug: 'kitchen',
    name: 'Kitchen',
    image: '/images/living-1.jpg',
    alt: 'Renovated white kitchen with marble backsplash, island seating and pendant lighting',
  },
  {
    slug: 'full-home',
    name: 'Full Home',
    image: '/images/interior-4.jpg',
    alt: 'Open-plan living room and kitchen opening onto a deck after a whole-home renovation',
  },
  {
    slug: 'bathroom',
    name: 'Bathroom',
    image: '/images/bathroom-1.jpg',
    alt: 'Renovated bathroom with a glass shower, freestanding tub and stone vanity',
  },
  {
    slug: 'cabinets',
    name: 'Cabinets',
    image: '/images/cabinets-1.jpg',
    alt: 'Handleless white cabinetry and integrated ovens in a renovated kitchen',
  },
  // No photograph is shown for these three. The library contains nothing that
  // genuinely depicts a finished basement, a Canadian home addition or a garden
  // suite — the files named for them are a ground-floor living room, a
  // Mediterranean villa with a pool, and a cabin in an Icelandic field. Showing
  // any of those would misrepresent the work, so these render as typographic
  // tiles until the real photography exists.
  { slug: 'basement', name: 'Basement', awaitingPhotography: true },
  { slug: 'home-additions', name: 'Home Additions', awaitingPhotography: true },
  { slug: 'garden-suite', name: 'Garden Suite', awaitingPhotography: true },
];

export const PROJECT_TYPES = [
  { id: 'kitchen', label: 'Kitchen Renovation', image: '/images/living-1.jpg', alt: 'Renovated white kitchen with marble backsplash and island seating' },
  { id: 'bathroom', label: 'Bathroom Renovation', image: '/images/bathroom-1.jpg', alt: 'Renovated bathroom with a glass shower and stone vanity' },
  { id: 'full-home', label: 'Full Home Renovation', image: '/images/interior-4.jpg', alt: 'Open-plan living room and kitchen opening onto a deck after a whole-home renovation' },
  { id: 'cabinets', label: 'Cabinet Renovation', image: '/images/cabinets-1.jpg', alt: 'Handleless white cabinetry and integrated ovens in a renovated kitchen' },
  { id: 'other', label: 'Other Project', image: '/images/interior-8.jpg', alt: 'Open-plan living and dining space opening onto a deck after renovation' },
  // See RENOVATION_TILES: no photograph in the library genuinely depicts these
  // three, so none is shown rather than showing the wrong room.
  { id: 'basement', label: 'Basement Renovation', awaitingPhotography: true },
  { id: 'addition', label: 'Home Addition', awaitingPhotography: true },
  { id: 'garden-suite', label: 'Garden Suite', awaitingPhotography: true },
];

export const TIMELINES = [
  'As soon as possible',
  'Within 1–3 months',
  'Within 3–6 months',
  '6+ months out',
  'Flexible / still planning',
];

export const BUDGETS = [
  'Under $15,000',
  '$15,000 – $35,000',
  '$35,000 – $75,000',
  '$75,000 – $150,000',
  '$150,000+',
  'Not sure yet',
];

export const PROPERTY_TYPES = ['Detached house', 'Semi-detached', 'Townhouse', 'Condo / apartment', 'Other'];

/**
 * The HyenaMind growth system: seven stages, one canonical definition.
 *
 * This is the single source of truth. The homepage engine, the services index,
 * the growth page and the about page all read from here, because the site
 * previously shipped two different frameworks — a seven-stage engine on the
 * homepage and a four-stage one everywhere else — and a visitor moving between
 * them was taught two different companies.
 *
 * `services` carries the annotated form the homepage engine renders.
 * `items` is the flat list the services and growth pages group by.
 */
export const GROWTH_STAGES = [
  {
    id: 'build',
    index: '01',
    title: 'Build',
    headline: 'A foundation built to convert',
    description:
      'The website, landing pages and tracking a local business is judged on, structured so every later stage has somewhere to send people.',
    services: [
      { name: 'Website design & development', note: 'The asset everything else points at.' },
      { name: 'Landing pages', note: 'A page per campaign, not one page for all of them.' },
      { name: 'Conversion-focused UX', note: 'Built around the enquiry, not the gallery.' },
      { name: 'Technical support', note: 'It keeps working after launch.' },
    ],
  },
  {
    id: 'get-found',
    index: '02',
    title: 'Get found',
    headline: 'Present where the search happens',
    description:
      'Before anyone can hire you they have to find you. Local presence and organic visibility are the part that keeps working when the ad budget stops.',
    services: [
      { name: 'Google Business Profile', note: 'The listing that decides local visibility.' },
      { name: 'Local SEO', note: 'Earning a place in the map results.' },
      { name: 'SEO', note: 'Visibility that compounds between campaigns.' },
      { name: 'Local presence', note: 'Consistent, findable, and reviewed.' },
    ],
  },
  {
    id: 'attract',
    index: '03',
    title: 'Attract',
    headline: 'Demand, created and captured',
    description:
      'Paid media reaches the people organic visibility has not, and reaches them again while the decision is still open.',
    services: [
      { name: 'Google Ads', note: 'Capture demand that already exists.' },
      { name: 'Meta Ads', note: 'Reach and retarget the right households.' },
      { name: 'TikTok Ads', note: 'Create attention before the search starts.' },
      { name: 'Social & retargeting', note: 'Stay visible through a long decision.' },
    ],
  },
  {
    id: 'convert',
    index: '04',
    title: 'Convert',
    headline: 'Attention becomes a real enquiry',
    description:
      'What happens after the click: the page has to earn the call or the form, and every enquiry has to be captured and attributed.',
    services: [
      { name: 'Landing pages & CRO', note: 'Turn visits into enquiries.' },
      { name: 'Lead capture', note: 'Ask for what qualifies, nothing more.' },
      { name: 'Call & enquiry tracking', note: 'Phone calls counted like form fills.' },
      { name: 'Analytics', note: 'Know what produced the enquiry.' },
    ],
  },
  {
    id: 'qualify',
    index: '05',
    title: 'Qualify',
    headline: 'Sorted before it reaches your calendar',
    description:
      'Not every enquiry is a job. Each one is checked for service, area and genuine intent, then routed to whoever should handle it.',
    services: [
      { name: 'Lead qualification', note: 'Service, area and intent, checked.' },
      { name: 'Lead routing', note: 'The right enquiry to the right person.' },
      { name: 'Appointment scheduling', note: 'Booked while the intent is live.' },
      { name: 'Matching', note: 'Work paired with the trade that fits it.' },
    ],
  },
  {
    id: 'follow-up',
    index: '06',
    title: 'Follow up',
    headline: 'Nothing goes cold in an inbox',
    description:
      'Most enquiries are lost to silence, not to competitors. Follow-up runs on its own so the first reply is not waiting on a spare evening.',
    services: [
      { name: 'CRM', note: 'One place every enquiry lives.' },
      { name: 'Email & SMS follow-up', note: 'A reply while the intent is still warm.' },
      { name: 'Lead nurturing', note: 'For the ones deciding over weeks.' },
      { name: 'Appointment reminders', note: 'Fewer quiet no-shows.' },
    ],
  },
  {
    id: 'grow',
    index: '07',
    title: 'Grow',
    headline: 'A system that compounds',
    description:
      'Every cycle produces data the next one is built on. Automation keeps it running, reporting shows what worked, optimisation moves the spend.',
    services: [
      { name: 'Marketing automation', note: 'The system runs without being nudged.' },
      { name: 'AI integrations', note: 'Applied where they remove real work.' },
      { name: 'Reporting', note: 'See which channel produced what.' },
      { name: 'Ongoing optimisation', note: 'Spend moves to what works.' },
    ],
  },
].map((s) => ({ ...s, items: s.services.map((x) => x.name) }));

export const HOMEOWNER_STEPS = [
 { title: 'Tell us about your project', text: 'Share the renovation you are planning: kitchen, bathroom, basement, full home, garden suite, cabinets, addition or something else.' },
 { title: 'Share your requirements', text: 'Location, scope, timeline and an approximate budget range. The essentials, nothing unnecessary.' },
  { title: 'Project review & qualification', text: 'Your project is reviewed and qualified so the right kind of professionals see it.' },
  { title: 'Suitable professionals identified', text: 'Renovation professionals matching your project type and area are identified.' },
  { title: 'Receive quotes & options', text: 'You receive quality, competitive quote options from suitable professionals.' },
 { title: 'Compare', text: 'Review your options side by side (scope, approach, fit) with clear information.' },
  { title: 'Choose the right fit', text: 'Move forward with the professional who is right for your project, with confidence.' },
];

export const LEAD_STEPS = [
 { title: 'Generate', text: 'Homeowner demand is generated across Google Ads, Meta, TikTok, SEO and social: targeted at real renovation intent.' },
  { title: 'Capture', text: 'Projects are captured with the details that matter: type, location, scope, timeline, budget where collected.' },
 { title: 'Qualify', text: 'Leads are qualified for relevance and intent: not sold as random, unfiltered contact lists.' },
  { title: 'Connect', text: 'Qualified homeowners are connected with suitable contractors, scheduled where applicable.' },
 { title: 'Opportunity', text: 'You receive a real renovation opportunity (context included) ready to quote and win.' },
];

export const WHY_POINTS = [
 { title: 'Renovation-focused', text: 'Everything we do is built for residential renovation, not generic local business marketing.' },
  { title: 'Local by design', text: 'Renovation is local. We build for real local markets, real housing stock and the way homeowners actually describe their projects.' },
 { title: 'Demand generation', text: 'We generate homeowner demand across search, social and local. We do not resell recycled lists.' },
 { title: 'Qualification over volume', text: 'Project type, location, scope, timeline and budget context: quality matters more than raw counts.' },
 { title: 'Full-stack growth', text: 'Build, get found, attract, convert, qualify, follow up, grow: one connected system instead of disconnected vendors.' },
  { title: 'Both sides understood', text: 'We understand homeowners planning renovations and contractors building businesses. That dual fluency is the platform.' },
];

// Stable entity ids. scripts/enrich-static-html.js emits the same ids in the static shell, so the
// static and runtime Organization/WebSite nodes describe one entity instead of two.
export const ORG_ID = 'https://hyenamind.com/#organization';
export const WEBSITE_ID = 'https://hyenamind.com/#website';
const ORG_REF = { '@type': 'Organization', '@id': ORG_ID, name: 'HyenaMind', url: 'https://hyenamind.com/' };

export const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'HyenaMind',
  url: 'https://hyenamind.com/',
  logo: 'https://hyenamind.com/assets/brand/hyena-emblem.png',
  description: SITE.description,
  email: SITE.email,
  telephone: '+1-236-857-3534',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Elliott St',
    addressLocality: 'Brampton',
    addressRegion: 'ON',
    postalCode: 'L6Y 1S9',
    addressCountry: 'CA',
  },
  // No areaServed until the real service area is confirmed; a placeholder value says nothing.
  slogan: 'Stop renting leads. Start owning your growth.',
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'HyenaMind',
  url: 'https://hyenamind.com/',
  publisher: ORG_REF,
};

export const articleLd = ({ title, description, path, image, datePublished }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  image: `https://hyenamind.com${image}`,
  ...(datePublished ? { datePublished } : {}),
  author: ORG_REF,
  publisher: {
    ...ORG_REF,
    logo: { '@type': 'ImageObject', url: 'https://hyenamind.com/assets/brand/hyena-emblem.png' },
  },
  mainEntityOfPage: `https://hyenamind.com${path}`,
});

export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `https://hyenamind.com${it.path}`,
  })),
});

export const faqLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const serviceLd = ({ name, description, path }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  provider: ORG_REF,
  url: `https://hyenamind.com${path}`,
});
